"use client";

import React, { useEffect, useRef, useState, use } from "react";
import Link from "next/link";
import Image from "next/image";
import { useCart } from "@/context/CartContext";
import type { Product, ProductVariant, ColorOption } from "@/types";
import { laadFirestore } from "@/lib/firestore-client"; // MOW-PERF-OKT26: Firestore pas laden als het nodig is
import SizeChartDrawer, { parseSizeChart } from "@/components/SizeChartDrawer";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import ReviewsSection from "@/components/ReviewsSection";
import { TagIcon, CheckIcon } from "@/components/Icons";
import { promotionForProduct, promoMessage } from "@/lib/promotions"; // MOW-OKT26 cart-actie
import ProductGallery from "@/components/ProductGallery";
import { stripHtml } from "@/lib/text";
import { parseRef, pickColorOption } from "@/lib/product-refs";
import { isProductZichtbaar, isVolledigUitverkocht } from "@/lib/product-visibility";
import { colorPhoto, colorPhotos } from "@/lib/product-images";
import ProductCard from "@/components/ProductCard";
import { getVariantPricing } from "@/lib/pricing";
import { sortSizes } from "@/lib/sizes";
import { focalPointStyle } from "@/lib/image-focal-point";
import { trackMetaEvent, catalogProduct } from "@/lib/meta-pixel";
import { klaviyoTrackViewedProduct, productCategorySlugs } from "@/lib/klaviyo-client";
import { useBijnaInBeeld } from "@/lib/use-load-near-view"; // MOW-PERF-OKT26

interface ProductPageProps {
  // Door de server opgehaald product (zie lib/product-initial.ts); voorkomt het
  // laadscherm en de bijbehorende layout shift. Null → client haalt het zelf op.
  initialProduct?: Product | null;
  params: Promise<{
    slug: string;
    // Alleen aanwezig als de pagina via de pad-gebaseerde kleur-URL
    // (/product/{slug}/{kleur}) geladen wordt — zie app/product/[slug]/[kleur]/page.tsx,
    // dat deze component hergebruikt met een extra route-segment.
    kleur?: string;
  }>;
}

function formatPrice(price: number) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(price);
}


// Max. aantal gerelateerde producten dat we tonen/ophalen (voorkomt extreme
// Firestore-reads bij een enorme lijst; curated upsell-lijsten zijn kleiner).
const RELATED_LIMIT = 12;

// Gerelateerde producten blok
type Entry = { raw: string; product: Product; option?: ColorOption };

function RelatedProducts({ ids, title = "Passend bij dit product" }: { ids: string[]; title?: string }) {
  const [products, setProducts] = React.useState<Entry[]>([]);
  // MOW-PERF-OKT26: de gerelateerde producten (en daarmee de Firestore-SDK) pas ophalen als dit blok
  // bijna in beeld komt, niet bij het laden van de pagina. Tot dan staat er een plaatshouder van 1 px.
  const [plaats, nabij] = useBijnaInBeeld<HTMLDivElement>();

  React.useEffect(() => {
    if (!nabij) return;
    (async () => {
      // Dedupe ruwe verwijzingen: dezelfde referentie kan per ongeluk dubbel in de
      // upsell-lijst staan (dirty data), wat React duplicate-key errors geeft.
      const uniqueIds = [...new Set(ids)];
      const wanted = uniqueIds.slice(0, RELATED_LIMIT).map((raw) => ({ raw, ...parseRef(raw) }));
      // Eén document per product ophalen, ook als er meerdere kleuren van hetzelfde
      // product in de lijst staan.
      const unieke = [...new Set(wanted.map((w) => w.slug))];
      const { db, doc, getDoc } = await laadFirestore();
      const snaps = await Promise.all(unieke.map((slug) => getDoc(doc(db, "products", slug))));
      const bySlug = new Map(snaps.filter((s) => s.exists()).map((s) => [s.id, { ...s.data(), id: s.id } as Product]));

      // Bewaar de volgorde zoals in de backend ingesteld; elke verwijzing wordt een
      // eigen kaart met de foto en naam van díe kleur.
      const kaarten: Entry[] = [];
      for (const w of wanted) {
        const product = bySlug.get(w.slug);
        if (!product) continue;
        if (!isProductZichtbaar(product)) continue;
        const option = pickColorOption(product.colorOptions, w.colorSlug);
        if (w.colorSlug && !option) continue; // verwijzing naar een kleur die niet meer bestaat
        if (isVolledigUitverkocht(product, option)) continue; // uitverkocht → niet aanraden
        kaarten.push({ raw: w.raw, product, option });
      }
      setProducts(kaarten);
    })();
  }, [ids, nabij]);

  if (products.length === 0) return <div ref={plaats} aria-hidden="true" style={{ height: "1px" }} />;

  return (
    <div style={{ marginTop: "64px", paddingTop: "40px", borderTop: "1px solid var(--color-gray-200)" }}>
      <h2 style={{ fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.06em", marginBottom: "24px", textTransform: "uppercase", color: "var(--color-gray-500)" }}>
        {title}
      </h2>
      <div className="products-grid">
        {products.map(({ raw, product: p, option }) => (
          // Dezelfde kaart als shop/categoriepagina's: toont dus ook automatisch
          // alle kleurbolletjes (niet alleen de gekoppelde kleur), foto-hover/swipe,
          // en de actieprijs via dezelfde prijs-logica als de rest van de site —
          // dat ontbrak allemaal in de eigen, vereenvoudigde kaart die hier stond.
          <ProductCard key={raw} product={p} colorOption={option} />
        ))}
      </div>
    </div>
  );
}

// Galerij voor een gekozen kleur: alleen aan die kleur gekoppelde foto's + algemene
// (niet-gekoppelde) foto's — nooit die van een andere kleur. Zonder kleur: alle foto's.
function galleryFor(product: Product, option: { name: string; images?: string[] } | null): string[] {
  return colorPhotos(product, option);
}

// Gedeelde interactieve pagina voor zowel /product/{slug} als de crawlbare
// kleur-URL /product/{slug}/{kleur} — beide "page.tsx"-bestanden importeren
// en renderen deze client component, elk met hun eigen server-side
// generateMetadata/JSON-LD (die een client component niet zelf kan leveren).
// Eerste keuze (kleur, maat, hoofdfoto) voor een product; zelfde regels als
// loadProduct, maar zonder window zodat server en client gelijk renderen.
function startSelectie(prod: Product, wantSlug: string | null | undefined) {
  const options = prod.colorOptions || [];
  const active = (wantSlug && options.find((o) => o.slug === wantSlug)) || options[0] || null;
  const sizes = active && active.sizes.length ? active.sizes : prod.sizes || [];
  return {
    image: galleryFor(prod, active)[0] || "",
    size: sizes[0] || "",
    color: active ? active.name : prod.colors && prod.colors.length > 0 ? prod.colors[0] : "",
  };
}

export default function ProductDetailPage({ params, initialProduct = null }: ProductPageProps) {
  const resolvedParams = use(params);
  const slug = resolvedParams.slug;
  const pathKleur = resolvedParams.kleur;

  const { addToCart, promotions } = useCart();
  const siteSettings = useSiteSettings();
  const [product, setProduct] = useState<Product | null>(initialProduct);
  const [loading, setLoading] = useState(!initialProduct);
  const [startSel] = useState(() => (initialProduct ? startSelectie(initialProduct, pathKleur) : null));
  const [selectedImage, setSelectedImage] = useState<string>(startSel?.image ?? "");

  // Selection states
  const [selectedSize, setSelectedSize] = useState<string>(startSel?.size ?? "");
  const [selectedColor, setSelectedColor] = useState<string>(startSel?.color ?? "");
  const [quantity, setQuantity] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<string>("description");
  const [cartSuccess, setCartSuccess] = useState<boolean>(false);
  const [sizeDrawerOpen, setSizeDrawerOpen] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertSuccess, setAlertSuccess] = useState(false);
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const zoomTouchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
    if (initialProduct) {
      // Server leverde het product al; alleen de oudere ?kleur=-link (niet te
      // zien op de server) nog toepassen.
      const q = !pathKleur ? new URLSearchParams(window.location.search).get("kleur") : null;
      if (q) {
        const sel = startSelectie(initialProduct, q);
        setSelectedImage(sel.image);
        setSelectedSize(sel.size);
        setSelectedColor(sel.color);
      }
    } else if (slug) {
      loadProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Meta Pixel ViewContent — één keer per geladen product (niet per re-render).
  useEffect(() => {
    if (!product) return;
    trackMetaEvent("ViewContent", {
      ...catalogProduct(product.id || slug),
      content_name: product.name,
      value: product.salePrice || product.price,
      currency: "EUR",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Klaviyo Viewed Product + "recent bekeken" — ook één keer per product, niet
  // opnieuw bij een kleurwissel. Staat vóór de early returns (hooks-regel),
  // dus kleur, variant en prijs hier zelf bepalen: dezelfde prijs als de
  // bezoeker ziet (getVariantPricing), niet de kale productprijs.
  useEffect(() => {
    if (!product) return;
    const options = product.colorOptions || [];
    const option = options.find((o) => o.name === selectedColor) || options[0] || null;
    const variant = option?.variants?.find((v) => v.size === selectedSize) || option?.variants?.[0];
    const pricing = getVariantPricing(product, variant);
    klaviyoTrackViewedProduct({
      productId: product.id || slug,
      slug,
      name: product.name,
      brand: product.brand,
      sku: variant?.sku || option?.sku || product.sku,
      categorySlugs: productCategorySlugs(product),
      image: colorPhoto(product, option),
      colorName: option?.name,
      colorSlug: option?.slug,
      price: pricing.price,
      originalPrice: pricing.originalPrice,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Toetsenbord in de zoomweergave: pijltjes om te bladeren, Escape om te sluiten.
  useEffect(() => {
    if (!zoomImage) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goToZoom(1);
      else if (e.key === "ArrowLeft") goToZoom(-1);
      else if (e.key === "Escape") setZoomImage(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // Bewust alleen zoomImage als dependency: galleryImages is pas verderop in
    // de component gedefinieerd (na de early returns hierboven) en zou hier
    // een "before initialization"-fout geven. goToZoom leest hem via closure;
    // elke navigatie ververst zoomImage, wat dit effect toch opnieuw laat lopen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomImage]);

  async function loadProduct() {
    try {
      const { db, doc, getDoc } = await laadFirestore();
      const docRef = doc(db, "products", slug);
      const snap = await getDoc(docRef);
      if (snap.exists() && isProductZichtbaar(snap.data())) {
        const prodData = snap.data() as Product;
        setProduct(prodData);

        const options = prodData.colorOptions || [];
        // Kleur uit het pad-segment (/product/{slug}/{kleur}, de crawlbare URL),
        // anders het oudere ?kleur=-queryformaat (bestaande links/bookmarks
        // blijven zo werken), anders de eerste kleuroptie.
        const wantSlug = pathKleur || (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("kleur") : null);
        const active = (wantSlug && options.find((o) => o.slug === wantSlug)) || options[0] || null;

        const imgs = galleryFor(prodData, active);
        setSelectedImage(imgs[0] || "");
        const sizes = active && active.sizes.length ? active.sizes : prodData.sizes || [];
        if (sizes.length > 0) setSelectedSize(sizes[0]);
        if (active) setSelectedColor(active.name);
        else if (prodData.colors && prodData.colors.length > 0) setSelectedColor(prodData.colors[0]);
      }
    } catch (err) {
      console.error("Fout bij laden product:", err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: "80px", paddingBottom: "80px", textAlign: "center", color: "var(--color-gray-400)" }}>
        <p>Productgegevens laden...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container" style={{ paddingTop: "80px", paddingBottom: "80px", textAlign: "center" }}>
        <h1 style={{ marginBottom: "20px" }}>Product niet gevonden</h1>
        <Link href="/shop" className="btn btn-secondary">
          Terug naar de winkel
        </Link>
      </div>
    );
  }

  // Actieve kleuroptie en de daarbij horende galerij/maten
  const options = product.colorOptions || [];
  const activeOption = options.find((o) => o.name === selectedColor) || null;
  const galleryImages = galleryFor(product, activeOption);
  const availableSizes = sortSizes(activeOption && activeOption.sizes.length ? activeOption.sizes : product.sizes || []);
  const sizeVariants = activeOption ? activeOption.variants : product.variants || [];

  // Per-kleur inhoud met terugval op productniveau: SEO-tekst, korte omschrijving
  // en upsells wisselen mee met de gekozen kleur (als die kleur eigen velden heeft).
  // Korte omschrijving is platte tekst; strip eventuele HTML uit de import.
  const activeShortDescription = stripHtml(activeOption?.shortDescription || product.shortDescription);
  // "Nieuw"-label: van het hele product, of specifiek van de gekozen kleur.
  const isNew = !!product.new || !!activeOption?.isNew;
  const isBestseller = !!product.bestseller;
  const isFeatured = !!product.featured;
  const activeUpsellIds = (activeOption?.upsellIds?.length ? activeOption.upsellIds
    : product.upsellIds?.length ? product.upsellIds
    : product.relatedProductIds) || [];

  function selectColor(opt: { name: string; slug?: string; images: string[]; sizes: string[] }) {
    setSelectedColor(opt.name);
    const imgs = product ? galleryFor(product, opt) : opt.images;
    setSelectedImage(imgs[0] || "");
    setSelectedSize(opt.sizes[0] || "");
    // Adresbalk bijwerken naar de crawlbare per-kleur-URL (/product/{slug}/{kleur})
    // zodat verversen/delen/kopiëren de gekozen kleur onthoudt. Bewust met de
    // kale browser-API i.p.v. de Next-router: /product/[slug] en
    // /product/[slug]/[kleur] zijn twee verschillende routes (matchen elk hun
    // eigen page.tsx), dus router.replace() zou bij elke kleurwissel de hele
    // pagina laten unmounten/opnieuw laden. Dit is puur een zichtbare
    // URL-update; er vindt geen navigatie plaats en de pagina blijft
    // gemonteerd — precies zoals de kleurwissel er al uitzag vóór deze URL's.
    if (typeof window !== "undefined") {
      const pad = opt.slug ? `/product/${slug}/${opt.slug}` : `/product/${slug}`;
      const query = new URLSearchParams(window.location.search);
      query.delete("kleur"); // zit nu in het pad, niet meer nodig als query-param
      const queryString = query.toString();
      window.history.replaceState(window.history.state, "", `${pad}${queryString ? `?${queryString}` : ""}`);
    }
  }

  // Doorbladeren binnen de geopende zoomweergave (pijltjes, swipe, toetsenbord).
  function goToZoom(delta: 1 | -1) {
    if (!zoomImage || galleryImages.length < 2) return;
    const i = galleryImages.indexOf(zoomImage);
    const next = (i + delta + galleryImages.length) % galleryImages.length;
    setZoomImage(galleryImages[next]);
    // MOW-OKT26 shop: hoofdfoto bladert mee, zodat je na het sluiten van de zoomweergave op dezelfde foto staat.
    setSelectedImage(galleryImages[next]);
  }

  // Find variation matching selections if any. Bij kleurproducten staan de
  // maat-varianten (incl. prijs-per-maat) in de varianten van de gekozen kleur.
  const currentVariant =
    sizeVariants.find(
      (v) => v.size === selectedSize && (!selectedColor || !v.color || v.color === selectedColor)
    ) ||
    product.variants?.find((v) => v.size === selectedSize && v.color === selectedColor);

  // Let op: geen useMemo hier — deze code staat ná de early returns hierboven,
  // dus een hook zou de hook-volgorde breken (React error #310). Gewone berekening.
  const isSelectedSizeSoldOut = (() => {
    if (!selectedSize) return false;
    const variantsForSelected = sizeVariants.filter((v) => v.size === selectedSize);
    const hasVariantDataForSelected = sizeVariants.some((v) => v.size === selectedSize);
    return hasVariantDataForSelected
      ? variantsForSelected.every((v) => (v.stock ?? 0) <= 0)
      : (product.stock ?? 1) <= 0;
  })();

  // Voorraadindicator: bij een gekozen maat de voorraad van die specifieke
  // variant, anders (product zonder maten/varianten) de totale productvoorraad.
  const lowStockCount = currentVariant ? currentVariant.stock : (availableSizes.length === 0 ? product.stock ?? null : null);

  async function handleRequestStockAlert(e: React.FormEvent) {
    e.preventDefault();
    if (!product || !alertEmail) return;
    try {
      const { db, collection, addDoc } = await laadFirestore();
      await addDoc(collection(db, "stockAlerts"), {
        email: alertEmail,
        productId: product.id,
        productName: product.name,
        color: selectedColor || "-",
        size: selectedSize || "-",
        createdAt: new Date().toISOString(),
        status: "pending",
      });
      setAlertSuccess(true);
      setAlertEmail("");
      setTimeout(() => setAlertSuccess(false), 5000);
    } catch (err) {
      console.error("Fout bij opslaan voorraadmelding:", err);
    }
  }

  const { price: activePrice, originalPrice, hasSale } = getVariantPricing(product, currentVariant);

  function handleAddToCart() {
    if (!product) return;
    
    // Add item to cart
    addToCart({
      productId: product.id,
      name: product.name,
      brand: product.brand,
      slug: product.slug,
      // MOW-OKT26 shop: galleryImages[0] i.p.v. de getoonde foto — sinds je door de hoofdfoto kunt vegen
      // zou anders een detailfoto het mandplaatje worden bij kleuren zonder eigen `images` (107 van 787).
      image: (activeOption && activeOption.images[0]) || galleryImages[0] || selectedImage || product.thumbnail || product.images[0] || "",
      price: activePrice,
      size: selectedSize || "-",
      color: selectedColor || "-",
      quantity: quantity,
      sku: currentVariant?.sku || product.sku,
      // Voor Klaviyo (kleur-URL en categorieën in Added to Cart/Started Checkout)
      colorSlug: activeOption?.slug,
      categorySlugs: productCategorySlugs(product),
    });

    setCartSuccess(true);
    setTimeout(() => setCartSuccess(false), 3000);
  }

  return (
    <div className="container product-detail-page" style={{ paddingTop: "30px", paddingBottom: "60px" }}>
      {/* Breadcrumbs */}
      <div style={{ fontSize: "0.8rem", color: "var(--color-gray-400)", marginBottom: "20px" }}>
        <Link href="/" style={{ color: "inherit", textDecoration: "none" }}>HOME</Link>
        {" / "}
        <Link href="/shop" style={{ color: "inherit", textDecoration: "none" }}>SHOP</Link>
        {" / "}
        <Link href={`/product-category/${product.categorySlug}`} style={{ color: "inherit", textDecoration: "none" }}>
          {product.category.toUpperCase()}
        </Link>
        {" / "}
        <span style={{ color: "var(--color-secondary)", fontWeight: 500 }}>
          {product.name.toUpperCase()}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "40px", marginBottom: "50px" }}>
        {/* LINKS: AFBEELDINGEN */}
        <div>
          {/* >>> MOW-OKT26 shop: hoofdfoto = swipebare scroll-snap-carrousel (components/ProductGallery.tsx).
              Thumbnails, zoomweergave en winkelmand blijven op selectedImage/galleryImages leunen. */}
          <ProductGallery
            images={galleryImages}
            selectedImage={selectedImage}
            onSelect={setSelectedImage}
            onZoom={setZoomImage}
            zoomOpen={!!zoomImage}
            productName={product.name}
            resetKey={selectedColor}
            badge={
              <>
                {hasSale && (
                  <span className="product-card-badge" style={{ position: "absolute", top: "15px", left: "15px", zIndex: 5 }}>sale</span>
                )}
                {isNew && !hasSale && (
                  <span className="product-card-badge badge-nieuw" style={{ position: "absolute", top: "15px", left: "15px", zIndex: 5 }}>nieuw</span>
                )}
                {isBestseller && !hasSale && !isNew && (
                  <span className="product-card-badge badge-bestseller" style={{ position: "absolute", top: "15px", left: "15px", zIndex: 5 }}>bestseller</span>
                )}
                {isFeatured && !hasSale && !isNew && !isBestseller && (
                  <span className="product-card-badge badge-uitgelicht" style={{ position: "absolute", top: "15px", left: "15px", zIndex: 5 }}>uitgelicht</span>
                )}
              </>
            }
          />
          {/* <<< MOW-OKT26 shop */}
          
          {/* Gallery Thumbnails */}
          {galleryImages.length > 1 && (
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {galleryImages.map((img, idx) => (
                <button
                  type="button"
                  key={idx}
                  onClick={() => setSelectedImage(img)}
                  aria-label={`Toon foto ${idx + 1} van ${galleryImages.length}`}
                  aria-current={selectedImage === img ? "true" : undefined}
                  style={{
                    position: "relative", width: "70px",
                    height: "70px",
                    border: selectedImage === img ? "2px solid var(--color-primary)" : "1px solid var(--color-gray-200)",
                    padding: "2px",
                    background: "#fff",
                    cursor: "pointer"
                  }}
                >
                  <Image src={img} alt={`${product.name} afbeelding ${idx + 1}`} fill sizes="80px" style={{ objectFit: "cover", ...focalPointStyle(img, product.imageFocalPoints) }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RECHTS: GEGEVENS */}
        <div>
          <span style={{ fontSize: "0.75rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--color-gray-400)", fontWeight: 600 }}>
            {product.brand}
          </span>
          <h1 style={{ fontSize: "2rem", margin: "8px 0 16px", color: "var(--color-secondary)" }}>
            {product.name}
          </h1>

          <div style={{ display: "flex", alignItems: "baseline", gap: "12px", marginBottom: "20px" }}>
            {originalPrice && (
              <span style={{ fontSize: "1.25rem", textDecoration: "line-through", color: "var(--color-gray-400)", marginRight: "4px" }}>
                {formatPrice(originalPrice)}
              </span>
            )}
            <span style={{ fontSize: "1.75rem", fontWeight: 600, color: "var(--color-price)" }}>
              {formatPrice(activePrice)}
            </span>
            <span style={{ fontSize: "0.8rem", color: "var(--color-gray-400)" }}>incl btw</span>
          </div>

          {/* >>> MOW-OKT26 cart-actie: actielabel (bijv. "2+1 gratis") met einddatum en uitleg, uit de actie-engine.
              promotionForProduct controleert zelf isPromotionLive, dus ook in een tab die over de einddag heen openstaat
              verdwijnt de uitleg op het moment dat het kaartlabel en de korting verdwijnen. */}
          {(() => {
            const actie = promotionForProduct({ brand: product.brand, slug: product.slug, id: product.id }, promotions);
            if (!actie) return null;
            const uitleg = promoMessage(actie);
            // endDate is een NL-kalenderdag (YYYY-MM-DD): als UTC-datum opmaken zodat de dag niet kan verschuiven
            const tot = actie.endDate && /^\d{4}-\d{2}-\d{2}$/.test(actie.endDate)
              ? new Date(`${actie.endDate}T00:00:00Z`).toLocaleDateString("nl-NL", { day: "numeric", month: "long", timeZone: "UTC" })
              : null;
            return (
              <div className="promo-notice is-uitleg" style={{ marginBottom: "20px" }}>
                <TagIcon size="1.15rem" />
                <span>
                  Nu in de actie: <strong>{actie.label || actie.name}</strong>{tot && <> · <span style={{ whiteSpace: "nowrap" }}>t/m {tot}</span></>}
                  {uitleg && <span className="promo-notice-uitleg">{uitleg}</span>}
                </span>
              </div>
            );
          })()}
          {/* <<< MOW-OKT26 cart-actie */}

          {activeShortDescription && (
            <p style={{ color: "var(--color-gray-600)", fontSize: "0.95rem", lineHeight: 1.6, marginBottom: "24px" }}>
              {activeShortDescription}
            </p>
          )}

          {/* ATTRIBUTEN SELECTIE */}
          <div style={{ borderTop: "1px solid var(--color-gray-100)", borderBottom: "1px solid var(--color-gray-100)", padding: "20px 0", marginBottom: "24px" }}>
            {options.length > 1 && (
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: "8px" }}>
                  KLEUR: <span style={{ color: "var(--color-primary)" }}>{selectedColor}</span>
                </label>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {options.map((opt) => {
                    const active = opt.name === selectedColor;
                    return (
                      <button
                        key={opt.slug}
                        type="button"
                        onClick={() => selectColor(opt)}
                        title={opt.name}
                        style={{
                          position: "relative", width: "52px", height: "62px", border: active ? "2px solid var(--color-primary)" : "1px solid var(--color-gray-200)",
                          borderRadius: "4px", overflow: "hidden", background: "#fff", cursor: "pointer", padding: 0,
                        }}
                      >
                        {opt.images?.[0] ? (
                          <Image src={opt.images[0]} alt={opt.name} fill sizes="52px" style={{ objectFit: "contain" }} />
                        ) : (
                          <span style={{ display: "flex", width: "100%", height: "100%", alignItems: "center", justifyContent: "center", fontSize: "0.6rem", color: "var(--color-gray-400)", textAlign: "center", padding: "2px" }}>{opt.name}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {availableSizes.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: "8px" }}>
                  MAAT
                </label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {availableSizes.map((size) => {
                    // Voorraad per maat uit de varianten van de gekozen kleur.
                    const variantsForSize = sizeVariants.filter((v) => v.size === size);
                    const hasVariantData = sizeVariants.some((v) => v.size === size);
                    const soldOut = hasVariantData
                      ? variantsForSize.every((v) => (v.stock ?? 0) <= 0)
                      : (product.stock ?? 1) <= 0;
                    return (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        title={soldOut ? "Tijdelijk uitverkocht - Klik om melding te ontvangen" : undefined}
                        style={{
                          padding: "6px 14px",
                          border: selectedSize === size ? "2px solid var(--color-primary)" : "1px solid var(--color-gray-200)",
                          background: soldOut 
                            ? (selectedSize === size ? "var(--color-primary-light, #fdf6f6)" : "var(--color-gray-50)")
                            : selectedSize === size ? "var(--color-primary-light, #fdf6f6)" : "#fff",
                          color: soldOut ? "var(--color-gray-400)" : selectedSize === size ? "var(--color-primary)" : "var(--color-gray-700)",
                          fontSize: "0.825rem",
                          cursor: "pointer",
                          fontWeight: selectedSize === size ? 600 : 400,
                          textDecoration: soldOut ? "line-through" : "none",
                          position: "relative",
                        }}
                      >
                        {size}{soldOut ? " (uitverkocht)" : ""}
                      </button>
                    );
                  })}
                </div>
                {/* Voorraadindicator: alleen tonen bij 10 of minder op voorraad */}
                {lowStockCount != null && lowStockCount > 0 && lowStockCount <= 10 && (
                  <div style={{ color: "var(--color-warning)", fontSize: "0.85rem", marginTop: "10px", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
                    <span aria-hidden style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--color-warning)", flexShrink: 0 }} />
                    <span>Nog {lowStockCount} {lowStockCount === 1 ? "stuk" : "stuks"} op voorraad</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {isSelectedSizeSoldOut ? (
            <div style={{ background: "var(--color-gray-50)", padding: "20px", border: "1px dashed var(--color-gray-300)", borderRadius: "6px", width: "100%" }}>
              <div style={{ fontSize: "0.85rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-secondary)", marginBottom: "8px" }}>
                TIJDELIJK UITVERKOCHT
              </div>
              <p style={{ fontSize: "0.85rem", color: "var(--color-gray-600)", marginBottom: "15px", lineHeight: 1.5 }}>
                Laat je e-mailadres achter en ontvang automatisch een bericht zodra de maat <strong>{selectedSize}</strong> in de kleur <strong>{selectedColor}</strong> weer op voorraad is!
              </p>
              {alertSuccess ? (
                <div className="promo-notice is-behaald">
                  <CheckIcon size="1.05rem" />
                  <span>Melding ingesteld. We mailen je zodra de voorraad is bijgewerkt.</span>
                </div>
              ) : (
                <form onSubmit={handleRequestStockAlert} style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <input
                    type="email"
                    required
                    placeholder="Jouw e-mailadres"
                    value={alertEmail}
                    onChange={(e) => setAlertEmail(e.target.value)}
                    style={{ flex: 1, minWidth: "200px", padding: "10px 14px", border: "1px solid var(--color-gray-300)", borderRadius: "4px", fontSize: "0.9rem" }}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ padding: "10px 20px", fontSize: "0.85rem", fontWeight: 600 }}
                  >
                    Houd mij op de hoogte
                  </button>
                </form>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", gap: "15px", alignItems: "center", flexWrap: "wrap", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--color-gray-200)", height: "46px" }}>
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  style={{ width: "35px", height: "100%", background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem" }}
                >
                  -
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{ width: "45px", height: "100%", border: "none", textAlign: "center", fontSize: "0.95rem", fontWeight: 600 }}
                />
                <button
                  onClick={() => setQuantity(q => q + 1)}
                  style={{ width: "35px", height: "100%", background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem" }}
                >
                  +
                </button>
              </div>

              <button
                onClick={handleAddToCart}
                className="btn btn-primary"
                style={{ height: "46px", padding: "0 36px", textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.85rem", fontWeight: 700, flex: 1, minWidth: "200px" }}
              >
                In winkelwagen
              </button>
            </div>
          )}

          {/* Delivery Promise Badge */}
          <div style={{ marginTop: "15px", fontSize: "0.85rem", color: "var(--color-gray-600)", display: "flex", alignItems: "center", gap: "8px" }}>
            <CheckIcon size="1rem" style={{ color: "#137333" }} />
            <span>
              {(() => {
                const now = new Date();
                const day = now.getDay(); // 0 = Sunday, 6 = Saturday
                const hour = now.getHours();
                if (day === 0 || day === 6) {
                  return "Vandaag besteld, maandag verzonden!";
                } else if (hour < 16) {
                  return "Voor 16:00 uur besteld, vandaag verzonden!";
                } else {
                  return "Vandaag besteld, morgen verzonden!";
                }
              })()}
            </span>
          </div>

          {/* USP's onder de winkelwagenknop */}
          <ul style={{ marginTop: "12px", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "7px", fontSize: "0.85rem", color: "var(--color-gray-600)" }}>
            <li style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CheckIcon size="1rem" style={{ color: "#137333" }} />
              <span>{siteSettings.returnDays} dagen retourmogelijkheid</span>
            </li>
            <li style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CheckIcon size="1rem" style={{ color: "#137333" }} />
              <span>Gratis verzending vanaf {siteSettings.shippingFreeFrom}</span>
            </li>
          </ul>

          {cartSuccess && (
            <div style={{ marginTop: "15px", padding: "12px", background: "#e6f4ea", color: "#137333", fontSize: "0.875rem", display: "flex", alignItems: "center", gap: "8px", border: "1px solid #c2e7c9" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span>Product is toegevoegd aan uw winkelwagen!</span>
            </div>
          )}

          {/* SKU / Categorie */}
          <div style={{ marginTop: "30px", fontSize: "0.8rem", color: "var(--color-gray-400)", lineHeight: 1.8 }}>
            {(currentVariant?.sku || product.sku) && (
              <div>Artikelnummer (SKU): <span style={{ color: "var(--color-gray-700)" }}>{currentVariant?.sku || product.sku}</span></div>
            )}
            <div>Categorie: <Link href={`/product-category/${product.categorySlug}`} style={{ color: "inherit", textDecoration: "underline" }}>{product.category}</Link></div>
            {product.brand && (
              <div>Merk: <span style={{ color: "var(--color-gray-700)" }}>{product.brand}</span></div>
            )}
          </div>
        </div>
      </div>

      {/* TABS SECTIE */}
      <div style={{ marginTop: "60px", borderTop: "1px solid var(--color-gray-200)" }}>
        {/* Tab-knoppen */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--color-gray-200)", overflowX: "auto", gap: 0 }}>
          {(["description", "info", "shipping", "sizing", "reviews"] as const).map((tab) => {
            const labels: Record<string, string> = {
              description: "Beschrijving",
              info: "Aanvullende informatie",
              shipping: "Bezorgen & Retourneren",
              sizing: "Maatadvies",
              reviews: "Beoordelingen",
            };
            const active = activeTab === tab;
            return (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{
                padding: "15px 22px", background: "none", border: "none", whiteSpace: "nowrap",
                borderBottom: active ? "2px solid var(--color-primary)" : "2px solid transparent",
                color: active ? "var(--color-secondary)" : "var(--color-gray-400)",
                fontWeight: active ? 600 : 500, cursor: "pointer",
                fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em"
              }}>{labels[tab]}</button>
            );
          })}
        </div>

        <div style={{ padding: "30px 0" }}>
          {/* Beschrijving */}
          {activeTab === "description" && (
            <div className="product-description-content"
              style={{ fontSize: "0.95rem", lineHeight: 1.8, color: "var(--color-gray-600)", maxWidth: "720px" }}
              dangerouslySetInnerHTML={{ __html: activeOption?.description || product.description }}
            />
          )}

          {/* Aanvullende informatie */}
          {activeTab === "info" && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", maxWidth: "560px" }}>
              <tbody>
                <tr style={{ borderBottom: "1px solid var(--color-gray-100)" }}>
                  <td style={{ padding: "12px 0", fontWeight: 600, width: "180px" }}>Merk</td>
                  <td style={{ padding: "12px 0", color: "var(--color-gray-600)" }}>{product.brand}</td>
                </tr>
                {product.materials && product.materials.length > 0 && (
                  <tr style={{ borderBottom: "1px solid var(--color-gray-100)" }}>
                    <td style={{ padding: "12px 0", fontWeight: 600 }}>Materiaal</td>
                    <td style={{ padding: "12px 0", color: "var(--color-gray-600)" }}>{product.materials.join(", ")}</td>
                  </tr>
                )}
                {product.fit && (
                  <tr style={{ borderBottom: "1px solid var(--color-gray-100)" }}>
                    <td style={{ padding: "12px 0", fontWeight: 600 }}>Pasvorm</td>
                    <td style={{ padding: "12px 0", color: "var(--color-gray-600)" }}>{product.fit}</td>
                  </tr>
                )}
                {product.pack && (
                  <tr style={{ borderBottom: "1px solid var(--color-gray-100)" }}>
                    <td style={{ padding: "12px 0", fontWeight: 600 }}>Verpakking</td>
                    <td style={{ padding: "12px 0", color: "var(--color-gray-600)" }}>{product.pack}</td>
                  </tr>
                )}
                <tr style={{ borderBottom: "1px solid var(--color-gray-100)" }}>
                  <td style={{ padding: "12px 0", fontWeight: 600 }}>Doelgroep</td>
                  <td style={{ padding: "12px 0", color: "var(--color-gray-600)", textTransform: "capitalize" }}>{product.gender}</td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Bezorgen & Retourneren */}
          {activeTab === "shipping" && (
            <div style={{ fontSize: "0.95rem", lineHeight: 1.8, color: "var(--color-gray-600)", maxWidth: "680px" }}>
              <h3 style={{ fontWeight: 600, color: "var(--color-secondary)", marginBottom: "12px" }}>Bezorging</h3>
              <ul style={{ paddingLeft: "1.4rem", listStyleType: "disc", marginBottom: "20px" }}>
                <li>Gratis verzending bij bestellingen vanaf {siteSettings.shippingFreeFrom}</li>
                <li>Onder {siteSettings.shippingFreeFrom} rekenen wij {siteSettings.shippingCost} verzendkosten</li>
                <li>Besteld op werkdagen voor {siteSettings.shippingCutoff} → volgende werkdag in huis</li>
                <li>We verzenden via {siteSettings.shippingCarrier}; je ontvangt een track &amp; trace per e-mail</li>
              </ul>
              <h3 style={{ fontWeight: 600, color: "var(--color-secondary)", marginBottom: "12px" }}>Retourneren</h3>
              <ul style={{ paddingLeft: "1.4rem", listStyleType: "disc", marginBottom: "20px" }}>
                <li>Je hebt {siteSettings.returnDays} dagen de tijd om een artikel te retourneren</li>
              </ul>
              {siteSettings.returnConditions && (
                <p style={{ marginBottom: "12px" }}>{siteSettings.returnConditions}</p>
              )}
              {siteSettings.hygieneNote && (
                <p style={{ fontSize: "0.85rem", color: "var(--color-gray-400)" }}>{siteSettings.hygieneNote}</p>
              )}
            </div>
          )}

          {/* Maatadvies */}
          {activeTab === "sizing" && (() => {
            const chartData = parseSizeChart(product.sizeChart || "");
            return (
              <div style={{ fontSize: "0.95rem", lineHeight: 1.8, color: "var(--color-gray-600)", maxWidth: "720px" }}>
                {chartData ? (
                  <>
                    {/* Compacte preview van eerste 3 rijen */}
                    <div style={{ overflowX: "auto", marginBottom: "16px", borderRadius: "8px", border: "1px solid var(--color-gray-200)" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                        <thead>
                          <tr style={{ background: "var(--color-gray-50)" }}>
                            {chartData.columns.map((col, i) => (
                              <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, borderBottom: "2px solid var(--color-gray-200)", whiteSpace: "nowrap" }}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {chartData.rows.slice(0, 3).map((row, ri) => (
                            <tr key={ri} style={{ borderBottom: "1px solid var(--color-gray-100)", background: ri % 2 === 0 ? "#fff" : "var(--color-gray-50)" }}>
                              {row.map((cell, ci) => (
                                <td key={ci} style={{ padding: "9px 14px", fontWeight: ci === 0 ? 600 : 400, color: ci === 0 ? "var(--color-secondary)" : "var(--color-gray-600)" }}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button
                      onClick={() => setSizeDrawerOpen(true)}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        padding: "10px 20px", border: "2px solid var(--color-primary)",
                        borderRadius: "4px", background: "none", cursor: "pointer",
                        color: "var(--color-primary)", fontWeight: 600, fontSize: "0.875rem",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-primary)"; e.currentTarget.style.color = "#fff"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--color-primary)"; }}
                    >
                      Bekijk volledige maattabel →
                    </button>
                  </>
                ) : (
                  <div style={{ padding: "32px 0", color: "var(--color-gray-400)", fontSize: "0.9rem" }}>
                    Voor dit product is nog geen maatadvies beschikbaar.{" "}
                    <a href="/contact-ons" style={{ color: "var(--color-primary)", textDecoration: "underline" }}>Neem contact op</a> bij twijfel.
                  </div>
                )}
                <h3 style={{ fontWeight: 600, color: "var(--color-secondary)", marginBottom: "10px", marginTop: "28px", fontSize: "0.95rem" }}>Hoe meet ik mijn maat?</h3>
                <ul style={{ paddingLeft: "1.4rem", listStyleType: "disc" }}>
                  <li><strong>Tailleomtrek</strong> — meet rond het smalste deel van je taille</li>
                  <li><strong>Heupomtrek</strong> — meet rond het breedste deel van je heupen</li>
                  <li>Twijfel je tussen twee maten? Kies dan de grotere maat</li>
                </ul>
              </div>
            );
          })()}

          {/* Reviews */}
          {activeTab === "reviews" && (
            <ReviewsSection productId={product.id} />
          )}
        </div>
      </div>

      {/* UPSELLS — "Misschien vind je dit ook leuk"; per kleur, met terugval op productniveau */}
      {activeUpsellIds.length ? (
        <RelatedProducts
          ids={activeUpsellIds}
          title={(activeOption?.upsellIds?.length || product.upsellIds?.length) ? "Misschien vind je dit ook leuk" : "Passend bij dit product"}
        />
      ) : null}

      {/* LIGHTBOX ZOOM MODAL */}
      {zoomImage && (
        <div
          onClick={() => setZoomImage(null)}
          onTouchStart={(e) => { zoomTouchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (zoomTouchStartX.current === null || galleryImages.length < 2) return;
            const dx = e.changedTouches[0].clientX - zoomTouchStartX.current;
            if (Math.abs(dx) > 40) goToZoom(dx < 0 ? 1 : -1);
            zoomTouchStartX.current = null;
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.85)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            padding: "20px"
          }}
        >
          {/* Geen vaste 90vw/90vh-doos meer: die gaf letterboxing (witruimte
              boven/onder) zodra de beeldverhouding niet exact paste. De
              afbeelding krijgt nu zijn eigen natuurlijke verhouding binnen de
              beschikbare ruimte. */}
          <Image
            src={zoomImage}
            alt={product.name}
            width={1200}
            height={1600}
            sizes="94vw"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "auto", height: "auto", maxWidth: "94vw", maxHeight: "88vh", objectFit: "contain", cursor: "default" }}
          />
          {galleryImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goToZoom(-1); }}
                aria-label="Vorige foto"
                style={{ position: "absolute", top: "50%", left: "16px", transform: "translateY(-50%)", background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", width: "44px", height: "44px", borderRadius: "50%", fontSize: "1.4rem", cursor: "pointer" }}
              >
                ‹
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goToZoom(1); }}
                aria-label="Volgende foto"
                style={{ position: "absolute", top: "50%", right: "16px", transform: "translateY(-50%)", background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", width: "44px", height: "44px", borderRadius: "50%", fontSize: "1.4rem", cursor: "pointer" }}
              >
                ›
              </button>
              <div style={{ position: "absolute", bottom: "20px", left: "50%", transform: "translateX(-50%)", color: "#fff", fontSize: "0.8rem", background: "rgba(0,0,0,0.5)", padding: "4px 12px", borderRadius: "12px" }}>
                {galleryImages.indexOf(zoomImage) + 1} / {galleryImages.length}
              </div>
            </>
          )}
          <button
            onClick={() => setZoomImage(null)}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: "2rem",
              cursor: "pointer"
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* MAATTABEL DRAWER */}
      {(() => {
        const chartData = parseSizeChart(product.sizeChart || "");
        return chartData ? (
          <SizeChartDrawer
            open={sizeDrawerOpen}
            onClose={() => setSizeDrawerOpen(false)}
            data={chartData}
            productName={product.name}
          />
        ) : null;
      })()}

      {/* Mobiele sticky 'in winkelwagen'-balk */}
      <div className="mobile-sticky-cart">
        <div style={{ minWidth: 0 }}>
          <div className="msc-name">{product.name}{selectedColor ? ` · ${selectedColor}` : ""}{selectedSize ? ` · ${selectedSize}` : ""}</div>
          <div className="msc-price">{formatPrice(activePrice)}</div>
        </div>
        <button onClick={handleAddToCart} className="btn btn-primary" style={{ flex: "0 0 auto", height: "46px", padding: "0 22px", textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.8rem" }}>
          In winkelwagen
        </button>
      </div>
    </div>
  );
}
